import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, X, ArrowLeft, BriefcaseBusiness, ShieldCheck, Mail, Phone, Calendar, FileText, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const Pill = ({ active, onClick, children, badge, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition flex items-center gap-1.5 ${active ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
    {children}
    {badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-700 text-white text-[10px] font-bold">{badge}</span>}
  </button>
);

const StatusBadge = ({ status }) => {
  const s = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-800' }[status] || 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${s}`}>{status}</span>;
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso || '—'; } };

const RequestCard = ({ req, onApprove, onReject }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState(req.kind === 'tutor' ? 'tutor' : (req.requested_role || 'staff'));

  const handle = async (action) => {
    setBusy(true);
    try {
      if (action === 'approve') await onApprove(req.id, { notes: notes.trim() || null, role });
      else await onReject(req.id, { notes: notes.trim() || null });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={`req-card-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${req.kind === 'tutor' ? 'bg-black text-[#D3D86F]' : 'bg-red-700 text-white'}`}>
            {req.kind === 'tutor' ? <BriefcaseBusiness className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-base font-bold text-gray-900 truncate">{req.full_name}</div>
              <StatusBadge status={req.status} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{req.kind}</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{req.email}</span>
              {req.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{req.phone}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmt(req.created_at)}</span>
            </div>
            {req.kind === 'tutor' && (
              <div className="text-xs text-gray-700 mt-2"><strong>Subjects:</strong> {req.subjects}</div>
            )}
            {req.kind === 'staff' && (
              <div className="text-xs text-gray-700 mt-2"><strong>Requested role:</strong> {req.requested_role}{req.department ? ` · ${req.department}` : ''}</div>
            )}
          </div>
        </div>
        {req.status === 'pending' && (
          <button onClick={() => setOpen((o) => !o)} className="h-9 px-4 rounded-full bg-black text-white text-[11px] font-bold uppercase tracking-wider" data-testid={`req-action-${req.id}`}>
            {open ? 'Close' : 'Review'}
          </button>
        )}
      </div>

      {open && req.status === 'pending' && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3" data-testid={`req-review-${req.id}`}>
          {req.kind === 'tutor' && (
            <div className="text-[12px] text-gray-700 space-y-1">
              {req.qualifications && <div><strong>Qualifications:</strong> {req.qualifications}</div>}
              {req.years_experience !== null && req.years_experience !== undefined && <div><strong>Years experience:</strong> {req.years_experience}</div>}
              {req.teaching_mode && <div><strong>Mode:</strong> {req.teaching_mode}</div>}
              {req.availability && <div><strong>Availability:</strong> {req.availability}</div>}
              {req.hourly_rate && <div><strong>Hourly rate:</strong> {req.hourly_rate}</div>}
              {req.short_bio && <div><strong>Bio:</strong> {req.short_bio}</div>}
              <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
                <FileText className="h-3 w-3 flex-shrink-0" />
                Approving will create a tutor portal account and email a temporary password. If a CV was also uploaded via /become-tutor, it lives in the V5 Tutor Applications tab.
              </div>
            </div>
          )}
          {req.kind === 'staff' && (
            <div className="text-[12px] text-gray-700 space-y-2">
              {req.reason && <div className="p-3 bg-gray-50 border border-gray-200 rounded"><strong>Reason:</strong><br />{req.reason}</div>}
              {req.invited_by && <div><strong>Invited by:</strong> {req.invited_by}</div>}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Assign role on approval</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs" data-testid={`req-role-${req.id}`}>
                  <option value="support">Support</option>
                  <option value="finance">Finance</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Notes (optional, sent in email if rejected)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={`req-notes-${req.id}`} placeholder="Optional notes" />
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button disabled={busy} onClick={() => handle('reject')} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider hover:bg-red-50 hover:text-red-700 flex items-center gap-1.5" data-testid={`req-reject-${req.id}`}>
              <X className="h-3 w-3" /> Reject
            </button>
            <button disabled={busy} onClick={() => handle('approve')} className="h-9 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid={`req-approve-${req.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Approve & create account
            </button>
          </div>
        </div>
      )}

      {req.status !== 'pending' && (
        <div className="mt-2 text-[11px] text-gray-500">
          {req.status === 'approved' && req.assigned_role && <>Provisioned as <strong className="text-gray-700">{req.assigned_role}</strong> on {fmt(req.decided_at)} by {req.decided_by}</>}
          {req.status === 'rejected' && <>Rejected on {fmt(req.decided_at)} by {req.decided_by}{req.notes_admin ? ` — ${req.notes_admin}` : ''}</>}
        </div>
      )}
    </div>
  );
};

const AdminAccessRequestsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [kind, setKind] = useState('tutor');
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [counts, setCounts] = useState({ tutor: 0, staff: 0 });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [cur, t, s] = await Promise.all([
        ax.get('/api/admin/access-requests', { params: { kind, status } }),
        ax.get('/api/admin/access-requests', { params: { kind: 'tutor', status: 'pending' } }),
        ax.get('/api/admin/access-requests', { params: { kind: 'staff', status: 'pending' } }),
      ]);
      setItems(cur.data.items || []);
      setCounts({ tutor: t.data.total || 0, staff: s.data.total || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load requests');
    } finally { setBusy(false); }
  }, [kind, status]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/admin/login', { replace: true }); return; }
    const role = (user.role || '').toLowerCase();
    if (!['admin', 'owner'].includes(role)) { navigate('/', { replace: true }); return; }
    load();
  }, [loading, user, navigate, load]);

  const approve = async (id, body) => {
    try {
      const r = await ax.post(`/api/admin/access-requests/${id}/approve`, body);
      toast.success(`Approved — account provisioned (${r.data.role}). Temp password emailed.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const reject = async (id, body) => {
    try {
      await ax.post(`/api/admin/access-requests/${id}/reject`, body);
      toast.success('Request rejected and applicant notified.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-access-requests-page">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE ADMIN</div>
            <div className="text-sm font-bold">Access Requests</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/admin/dashboard')} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="back-to-admin"><ArrowLeft className="h-3 w-3" /> Admin</button>
            <button onClick={() => navigate('/staff/dashboard')} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="nav-staff-hub"><MessageSquare className="h-3 w-3" /> Staff Hub</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill active={kind === 'tutor'} onClick={() => setKind('tutor')} testid="tab-tutor" badge={counts.tutor}><BriefcaseBusiness className="h-3.5 w-3.5" /> Tutor requests</Pill>
          <Pill active={kind === 'staff'} onClick={() => setKind('staff')} testid="tab-staff" badge={counts.staff}><ShieldCheck className="h-3.5 w-3.5" /> Staff requests</Pill>
          <div className="ml-auto flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 rounded-full border border-gray-200 bg-white text-xs font-bold uppercase tracking-wider" data-testid="status-filter">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {busy ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl text-center text-xs text-gray-400 italic py-12" data-testid="empty-state">
            No {status} {kind} requests.
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {items.map((r) => <RequestCard key={r.id} req={r} onApprove={approve} onReject={reject} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAccessRequestsPage;
